
import React, { useEffect, useState, useMemo } from 'react';
import { Label } from '@/components/ui/label';
import { 
  Select, 
  SelectContent, 
  SelectGroup, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { Poll } from '@/types/poll';
import { useCountry } from '@/contexts/country';
import { supabase } from '@/integrations/supabase/client';
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";

interface Constituency {
  id: string;
  constituency_name: string;
  constituency_code: string;
}

interface PollLocationTimeFieldsProps {
  currentPoll: Poll;
  updateField: (field: keyof Poll, value: any) => void;
}

const PollLocationTimeFields: React.FC<PollLocationTimeFieldsProps> = ({ currentPoll, updateField }) => {
  const { availableCountries } = useCountry();
  const [constituencies, setConstituencies] = useState<Constituency[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [lastSelectedCountry, setLastSelectedCountry] = useState<string | null>(currentPoll.country_code);

  console.log('PollLocationTimeFields rendering:', { 
    pollId: currentPoll?.id,
    country_code: currentPoll?.country_code,
    constituency_code: currentPoll?.constituency_code,
    constituencies: constituencies.length,
    lastSelectedCountry
  });

  // Find the country name for display in the select
  const selectedCountryName = useMemo(() => {
    if (!currentPoll.country_code) return '';
    const country = availableCountries.find(c => c.code === currentPoll.country_code);
    console.log('Country lookup:', { 
      looking_for: currentPoll.country_code,
      found: country ? true : false,
      countryName: country?.name
    });
    return country ? country.name : '';
  }, [currentPoll.country_code, availableCountries]);

  // Find the constituency name for display in the select
  const selectedConstituencyName = useMemo(() => {
    if (!currentPoll.constituency_code) return '';
    const constituency = constituencies.find(c => c.constituency_code === currentPoll.constituency_code);
    console.log('Constituency lookup:', { 
      looking_for: currentPoll.constituency_code,
      found: constituency ? true : false,
      constituencyName: constituency?.constituency_name
    });
    return constituency ? constituency.constituency_name : '';
  }, [currentPoll.constituency_code, constituencies]);
  
  const handleCountryChange = (value: string) => {
    const countryCode = value === 'all' ? null : value;
    console.log(`Setting country_code to:`, { 
      value, 
      countryCode,
      previousCode: currentPoll.country_code 
    });
    
    // Track the last selected country code
    setLastSelectedCountry(countryCode);
    
    // Update the country code
    updateField('country_code', countryCode);
    
    // Clear constituency when changing country
    if (countryCode === null || (currentPoll.country_code && countryCode !== currentPoll.country_code)) {
      console.log('Resetting constituency_code to null');
      updateField('constituency_code', null);
    }
  };
  
  // Fetch constituencies when country changes
  useEffect(() => {
    // Only refetch if country has changed or constituencies are empty for this country
    if (currentPoll.country_code !== lastSelectedCountry || 
        (currentPoll.country_code && constituencies.length === 0)) {
      
      console.log('Country change effect triggered:', currentPoll.country_code);
      setLastSelectedCountry(currentPoll.country_code);
      
      if (!currentPoll.country_code) {
        console.log('No country code, clearing constituencies');
        setConstituencies([]);
        return;
      }

      const fetchConstituencies = async () => {
        setIsLoading(true);
        try {
          console.log('Fetching constituencies for:', currentPoll.country_code);
          const { data, error } = await supabase
            .from('active_constituencies')
            .select('id, constituency_name, constituency_code')
            .eq('country_code', currentPoll.country_code)
            .order('constituency_name', { ascending: true });

          if (error) throw error;
          
          console.log(`Fetched ${data?.length || 0} constituencies`);
          setConstituencies(data || []);
          
          // If we have constituency_code, let's check if it's valid
          if (currentPoll.constituency_code) {
            const isValid = data?.some(c => c.constituency_code === currentPoll.constituency_code);
            console.log('Constituency validation:', { 
              code: currentPoll.constituency_code, 
              isValid
            });
            
            if (!isValid) {
              // Only reset if it's invalid for the selected country
              console.log('Resetting invalid constituency_code to null');
              updateField('constituency_code', null);
            }
          }
        } catch (error) {
          console.error('Error fetching constituencies:', error);
        } finally {
          setIsLoading(false);
        }
      };

      fetchConstituencies();
    }
  }, [currentPoll.country_code, updateField, constituencies.length, lastSelectedCountry]);
  
  // Parse closes_at date for calendar
  const closesAtDate = currentPoll.closes_at ? new Date(currentPoll.closes_at) : undefined;
  
  // Handle date selection
  const handleDateChange = (date: Date | undefined) => {
    let dateValue = null;
    if (date) {
      // Create a date at noon UTC to avoid timezone issues
      const newDate = new Date(date);
      newDate.setUTCHours(12, 0, 0, 0);
      dateValue = newDate.toISOString();
      console.log('Setting closes_at to:', dateValue);
    }
    updateField('closes_at', dateValue);
  };
  
  // Debug constituency dropdown
  useEffect(() => {
    console.log('Constituency dropdown state:', {
      pollConstituencyCode: currentPoll.constituency_code,
      availableConstituencies: constituencies.map(c => ({ 
        code: c.constituency_code, 
        name: c.constituency_name 
      })),
      selectedConstituencyName,
      disabled: !currentPoll.country_code || isLoading
    });
  }, [currentPoll.constituency_code, constituencies, selectedConstituencyName, currentPoll.country_code, isLoading]);
  
  return (
    <div className="grid grid-cols-2 gap-4">
      <div className="space-y-2">
        <Label htmlFor="country">Country (Optional)</Label>
        <Select 
          value={currentPoll.country_code || 'all'} 
          onValueChange={handleCountryChange}
        >
          <SelectTrigger id="country" className="bg-background">
            <SelectValue placeholder="All Countries">
              {selectedCountryName || "All Countries"}
            </SelectValue>
          </SelectTrigger>
          <SelectContent className="bg-white dark:bg-gray-800 z-50">
            <SelectGroup>
              <SelectItem value="all">All Countries</SelectItem>
              {availableCountries.filter(c => c.active).map((country) => (
                <SelectItem key={country.code} value={country.code}>
                  {country.name}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="constituency">Constituency (Optional)</Label>
        <Select
          value={currentPoll.constituency_code || 'all_constituencies'}
          onValueChange={(value) => {
            const constituencyCode = value === 'all_constituencies' ? null : value;
            console.log(`Setting constituency_code to:`, { 
              value, 
              constituencyCode,
              previousCode: currentPoll.constituency_code 
            });
            updateField('constituency_code', constituencyCode);
          }}
          disabled={!currentPoll.country_code || isLoading}
        >
          <SelectTrigger id="constituency" className="bg-background">
            <SelectValue placeholder="All Constituencies">
              {selectedConstituencyName || "All Constituencies"}
            </SelectValue>
          </SelectTrigger>
          <SelectContent className="bg-white dark:bg-gray-800 z-50">
            <SelectGroup>
              <SelectItem value="all_constituencies">All Constituencies</SelectItem>
              {constituencies.map((constituency) => (
                <SelectItem key={constituency.constituency_code} value={constituency.constituency_code}>
                  {constituency.constituency_name} ({constituency.constituency_code})
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="closes_at">Closing Date</Label>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              id="closes_at"
              variant="outline"
              className={cn(
                "w-full justify-start text-left font-normal bg-background",
                !closesAtDate && "text-muted-foreground"
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {closesAtDate ? format(closesAtDate, "MMM d, yyyy") : <span>Select closing date</span>}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0 pointer-events-auto" align="start">
            <Calendar
              mode="single"
              selected={closesAtDate}
              onSelect={handleDateChange}
              initialFocus
              className={cn("p-3 pointer-events-auto", "bg-popover border border-border rounded-md")}
            />
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
};

export default PollLocationTimeFields;
